// frontend/src/components/asset/AccidentDetailModal.js
/**
 * Custodian drill-down and action surface for a single vehicle accident.
 *
 * Shared by:
 *   - AssetAdminFleetAccidents (fleet-wide insurer-deadline queue)
 *   - AssetDetailModal → Accidents tab (per-vehicle history)
 *
 * Action panels are status / reporting-basis driven. The insurer clock banner
 * uses hours remaining (red overdue / amber within 12h); legacy imports show a
 * muted notice instead of a live clock.
 *
 * Theme: Admin (red). Props: accident (object with id), onClose, readOnly?,
 * onChangeDriver?, onViewVehicle?.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, AlertTriangle, Car, CalendarClock, CheckCircle2, FileText,
  Link2, MapPin, ShieldAlert, Upload, User, XCircle,
} from 'lucide-react';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import DatePickerField from '../UI/DatePickerField';
import { StatusBadge } from '../UI/StatusBadge';
import SecureFileLink from './SecureFileLink';
import api, { assetInventoryAPI } from '../../services/api';
import { formatDate, formatDateTime, DATE_PICKER_FORMAT, toLocalISODate } from '../../utils/dateUtils';
import {
  useVehicleAccident,
  useMarkInsurerNotified,
  useSetReportingBasis,
  useRecordPoliceReport,
  useUploadAccidentSas,
  useRecordAccidentClaim,
  useLinkAccidentRepair,
  useCloseAccident,
  useCancelAccident,
  useMaintenanceLogs,
} from '../../hooks/useAssetInventoryData';

/** Map model fault / claim_status onto statusConfig keys. */
export const accidentBadgeStatusFor = (status) => {
  if (!status) return status;
  if (status === 'shared') return 'shared_fault';
  if (status === 'undetermined') return 'fault_undetermined';
  return status;
};

export const claimBadgeStatusFor = (claimStatus) => {
  if (!claimStatus) return claimStatus;
  return `claim_${claimStatus}`;
};

const REPORTING_BASIS_OPTIONS = [
  { value: 'reporting_only', label: 'Reporting Only' },
  { value: 'third_party_claim', label: 'Third Party Claim' },
  { value: 'own_damage_claim', label: 'Own Damage Claim' },
  { value: 'no_reporting', label: 'No Reporting' },
];

const CLAIM_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_assessment', label: 'Under Assessment' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'settled', label: 'Settled' },
  { value: 'private_settled', label: 'Privately Settled' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const FAULT_OPTIONS = [
  { value: 'at_fault', label: 'At Fault' },
  { value: 'not_at_fault', label: 'Not At Fault' },
  { value: 'shared', label: 'Shared Fault' },
  { value: 'undetermined', label: 'Undetermined' },
];

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 min-w-0">
    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 break-words">{value || '—'}</p>
    </div>
  </div>
);

const hoursUntil = (iso) => {
  if (!iso) return null;
  return (new Date(iso) - new Date()) / 3600000;
};

/** Insurer 24h clock. Colour tracks urgency in hours, not status. */
const InsurerClockBanner = ({ accident }) => {
  if (accident.is_legacy_import) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
        Imported record — insurer clock does not apply.
      </div>
    );
  }
  if (accident.insurer_notified_at) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 flex items-center gap-2 text-green-900">
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm font-medium">
          Insurer notified {formatDateTime(accident.insurer_notified_at)}
          {accident.insurer_reference && ` · Ref ${accident.insurer_reference}`}
        </span>
      </div>
    );
  }
  if (!accident.insurer_report_deadline) return null;

  const hours = hoursUntil(accident.insurer_report_deadline);
  const overdue = hours < 0;
  const urgent = hours >= 0 && hours <= 12;
  const tone = overdue
    ? 'bg-red-50 border-red-200 text-red-800'
    : urgent
      ? 'bg-amber-50 border-amber-200 text-amber-800'
      : 'bg-gray-50 border-gray-200 text-gray-700';
  const text = overdue
    ? `Overdue by ${Math.abs(Math.round(hours))}h`
    : hours < 1
      ? `Due in ${Math.max(1, Math.round(hours * 60))}m`
      : `${Math.round(hours)}h remaining`;

  return (
    <div className={`rounded-xl border px-3 py-2 flex items-center gap-2 ${tone}`}>
      <CalendarClock className="w-4 h-4 flex-shrink-0" />
      <span className="text-sm font-medium">Insurer notification: {text}</span>
      <span className="text-xs opacity-75 ml-auto whitespace-nowrap">
        by {formatDateTime(accident.insurer_report_deadline)}
      </span>
    </div>
  );
};

const AccidentPhotoThumb = ({ accidentId, photo }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  // Prefer the relative helper — serializer photo_url is /api/v1/... which
  // double-prefixes against axios baseURL.
  const photoUrl = assetInventoryAPI.accidentPhotoUrl(accidentId, photo.id);

  useEffect(() => {
    let blobUrl;
    let cancelled = false;
    if (!photoUrl) return undefined;
    api.get(photoUrl, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setUrl(blobUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [photoUrl]);

  if (failed) {
    return (
      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <AlertCircle className="w-5 h-5 text-gray-400" />
      </div>
    );
  }

  return (
    <a
      href={url || undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 block hover:ring-2 hover:ring-red-500 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
      title={photo.caption || photo.photo_type_display || 'Photo'}
    >
      {url ? (
        <img src={url} alt={photo.caption || 'Accident photo'} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
        </div>
      )}
    </a>
  );
};

const AccidentDetailModal = ({
  isOpen,
  onClose,
  accident: accidentProp,
  readOnly = false,
  onChangeDriver,
  onViewVehicle,
}) => {
  const accidentId = accidentProp?.id;
  const { data: accident, isLoading } = useVehicleAccident(accidentId, {
    enabled: isOpen && !!accidentId,
  });

  const [insurerRef, setInsurerRef] = useState('');
  const [basis, setBasis] = useState('');
  const [noReportingReason, setNoReportingReason] = useState('');
  const [policeNumber, setPoliceNumber] = useState('');
  const [policeDate, setPoliceDate] = useState(null);
  const [sasFile, setSasFile] = useState(null);
  const [claimForm, setClaimForm] = useState({
    claim_status: '',
    claim_number: '',
    claim_amount: '',
    excess_amount: '',
    fault: '',
    ncd_affected: '',
    private_settlement_amount: '',
    video_url: '',
  });
  const [maintenanceLogId, setMaintenanceLogId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const markInsurer = useMarkInsurerNotified();
  const setReportingBasis = useSetReportingBasis();
  const recordPolice = useRecordPoliceReport();
  const uploadSas = useUploadAccidentSas();
  const recordClaim = useRecordAccidentClaim();
  const linkRepair = useLinkAccidentRepair();
  const closeAccident = useCloseAccident();
  const cancelAccident = useCancelAccident();

  const fleetVehicleId = accident?.fleet_vehicle;
  const { data: maintenanceData } = useMaintenanceLogs(
    fleetVehicleId
      ? { fleet_vehicle: fleetVehicleId, page_size: 50 }
      : { fleet_vehicle: -1, page_size: 1 },
  );
  const maintenanceOptions = useMemo(
    () => (maintenanceData?.results || []).map((log) => ({
      value: String(log.id),
      label: `${formatDate(log.service_date || log.created_at)} · ${log.workshop_name || log.service_type || `#${log.id}`}`,
    })),
    [maintenanceData],
  );

  useEffect(() => {
    if (!accident) return;
    setBasis(accident.reporting_basis || '');
    setNoReportingReason(accident.no_reporting_reason || '');
    setPoliceNumber(accident.police_report_number || '');
    setPoliceDate(accident.police_report_date ? new Date(accident.police_report_date) : null);
    setClaimForm({
      claim_status: accident.claim_status || '',
      claim_number: accident.claim_number || '',
      claim_amount: accident.claim_amount != null ? String(accident.claim_amount) : '',
      excess_amount: accident.excess_amount != null ? String(accident.excess_amount) : '',
      fault: accident.fault || '',
      ncd_affected: accident.ncd_affected == null ? '' : accident.ncd_affected ? 'true' : 'false',
      private_settlement_amount: accident.private_settlement_amount != null
        ? String(accident.private_settlement_amount) : '',
      video_url: accident.video_url || '',
    });
    setMaintenanceLogId(accident.maintenance_log ? String(accident.maintenance_log) : '');
  }, [accident]);

  const handleClose = () => {
    setInsurerRef('');
    setSasFile(null);
    setCancelReason('');
    setCancelling(false);
    onClose();
  };

  const status = accident?.status;
  const isOpenAccident = status && !['accident_closed', 'cancelled'].includes(status);
  const canAct = isOpenAccident && !readOnly;
  const showClaimPanel = ['third_party_claim', 'own_damage_claim'].includes(accident?.reporting_basis);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Vehicle Accident"
      size="lg"
      theme="admin"
      contentClassName="space-y-3"
      footer={
        <>
          {accident?.asset_id && onViewVehicle && (
            <button
              type="button"
              onClick={() => onViewVehicle(accident.asset_id)}
              className="btn btn-admin-outline"
            >
              <Car className="w-4 h-4 mr-2" />
              View Vehicle
            </button>
          )}
          <button type="button" onClick={handleClose} className="btn btn-admin-outline">
            Close
          </button>
        </>
      }
    >
      {isLoading || !accident ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
        </div>
      ) : (
        <>
          {/* Identity */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-4 h-4 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-900 truncate">
                  {accident.reference}
                </h2>
                <StatusBadge
                  status={accidentBadgeStatusFor(accident.status)}
                  label={accident.status_display}
                />
                {accident.reporting_basis && (
                  <StatusBadge
                    status={accident.reporting_basis}
                    label={accident.reporting_basis_display}
                  />
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {accident.accident_type_display}
                {accident.fault_display && ` · ${accident.fault_display}`}
              </p>
            </div>
          </div>

          {isOpenAccident && <InsurerClockBanner accident={accident} />}

          {/* Facts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3">
            <DetailRow icon={Car} label="Vehicle" value={accident.vehicle_number_raw} />
            <DetailRow
              icon={CalendarClock}
              label="Accident date & time"
              value={formatDateTime(accident.accident_datetime)}
            />
            <DetailRow icon={MapPin} label="Location" value={accident.location} />
            <DetailRow
              icon={User}
              label="Driver"
              value={
                accident.driver_display_name
                  ? `${accident.driver_display_name}${accident.driver_staff_id ? ` (${accident.driver_staff_id})` : ''}`
                  : '—'
              }
            />
          </div>

          {accident.description && (
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">{accident.description}</p>
            </div>
          )}

          {/* Driver change */}
          {canAct && onChangeDriver && (
            <button
              type="button"
              onClick={() => onChangeDriver(accident)}
              className="btn btn-admin-outline"
            >
              <User className="w-4 h-4 mr-2" />
              {accident.driver_display_name ? 'Change Driver' : 'Select Driver'}
            </button>
          )}

          {/* Mark insurer notified */}
          {canAct && status === 'reported' && !accident.insurer_notified_at && (
            <div className="space-y-2 rounded-xl border border-red-200 bg-red-50/50 p-3">
              <h3 className="text-sm font-semibold text-gray-900">Notify insurer (24h clock)</h3>
              <p className="text-xs text-gray-600">
                Call or email the insurer, then record it here to stop the clock.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={insurerRef}
                  onChange={(e) => setInsurerRef(e.target.value)}
                  placeholder="Insurer reference (optional)"
                  className="input-admin flex-1"
                />
                <button
                  type="button"
                  disabled={markInsurer.isPending}
                  onClick={() => markInsurer.mutate({
                    id: accident.id,
                    insurer_reference: insurerRef.trim(),
                  })}
                  className="btn btn-admin-primary"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark Notified
                </button>
              </div>
            </div>
          )}

          {/* Reporting basis */}
          {canAct && (
            <div className="space-y-2 rounded-xl border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-900">Reporting Basis</h3>
              <FormSelect
                options={REPORTING_BASIS_OPTIONS}
                value={basis}
                onChange={setBasis}
                placeholder="Select basis..."
                theme="admin"
              />
              {basis === 'no_reporting' && (
                <textarea
                  value={noReportingReason}
                  onChange={(e) => setNoReportingReason(e.target.value)}
                  rows={2}
                  placeholder="Why is this not being reported to the insurer?"
                  className="textarea-admin w-full"
                />
              )}
              <button
                type="button"
                disabled={
                  !basis
                  || (basis === 'no_reporting' && !noReportingReason.trim())
                  || setReportingBasis.isPending
                  || basis === accident.reporting_basis
                }
                onClick={() => setReportingBasis.mutate({
                  id: accident.id,
                  basis,
                  no_reporting_reason: noReportingReason.trim(),
                })}
                className="btn btn-admin-primary"
              >
                Save Reporting Basis
              </button>
            </div>
          )}

          {/* Police report */}
          {(accident.effective_police_report_required || accident.police_report_number) && (
            <div className="space-y-2 rounded-xl border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Traffic Police Report
              </h3>
              {canAct && !accident.police_report_number ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={policeNumber}
                    onChange={(e) => setPoliceNumber(e.target.value)}
                    placeholder="TP report number"
                    className="input-admin w-full"
                  />
                  <DatePickerField
                    selected={policeDate}
                    onChange={setPoliceDate}
                    dateFormat={DATE_PICKER_FORMAT}
                    maxDate={new Date()}
                    placeholderText="Report date"
                    theme="admin"
                    className="input-admin w-full"
                  />
                  <button
                    type="button"
                    disabled={!policeNumber.trim() || recordPolice.isPending}
                    onClick={() => recordPolice.mutate({
                      id: accident.id,
                      police_report_number: policeNumber.trim(),
                      police_report_date: policeDate ? toLocalISODate(policeDate) : undefined,
                    })}
                    className="btn btn-admin-primary sm:col-span-2"
                  >
                    Record Police Report
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-700">
                  {accident.police_report_number || '—'}
                  {accident.police_report_date && ` · ${formatDate(accident.police_report_date)}`}
                </p>
              )}
            </div>
          )}

          {/* SAS upload */}
          <div className="space-y-2 rounded-xl border border-gray-200 p-3">
            <h3 className="text-sm font-semibold text-gray-900">Singapore Accident Statement</h3>
            {accident.has_sas || accident.sas_document_url ? (
              <SecureFileLink
                url={assetInventoryAPI.accidentSasDocumentUrl(accident.id)}
                label="View SAS"
              />
            ) : canAct ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-gray-300 cursor-pointer hover:border-red-400 hover:bg-red-50/40 transition-colors flex-1 focus-within:ring-2 focus-within:ring-red-500">
                  <Upload className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600 truncate">
                    {sasFile ? sasFile.name : 'Choose SAS file...'}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(e) => setSasFile(e.target.files?.[0] || null)}
                    className="sr-only"
                  />
                </label>
                <button
                  type="button"
                  disabled={!sasFile || uploadSas.isPending}
                  onClick={() => uploadSas.mutate(
                    { id: accident.id, file: sasFile },
                    { onSuccess: () => setSasFile(null) },
                  )}
                  className="btn btn-admin-primary"
                >
                  Upload SAS
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500">No SAS uploaded yet.</p>
            )}
          </div>

          {/* Claim panel */}
          {showClaimPanel && (
            <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/40 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-gray-900">Claim</h3>
                {accident.claim_status && (
                  <StatusBadge
                    status={claimBadgeStatusFor(accident.claim_status)}
                    label={accident.claim_status_display}
                  />
                )}
              </div>
              {canAct ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <FormSelect
                    options={CLAIM_STATUS_OPTIONS}
                    value={claimForm.claim_status}
                    onChange={(v) => setClaimForm((p) => ({ ...p, claim_status: v }))}
                    theme="admin"
                    placeholder="Claim status..."
                  />
                  <FormSelect
                    options={FAULT_OPTIONS}
                    value={claimForm.fault}
                    onChange={(v) => setClaimForm((p) => ({ ...p, fault: v }))}
                    theme="admin"
                    placeholder="Fault..."
                  />
                  <input
                    type="text"
                    value={claimForm.claim_number}
                    onChange={(e) => setClaimForm((p) => ({ ...p, claim_number: e.target.value }))}
                    placeholder="Claim number"
                    className="input-admin w-full"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={claimForm.claim_amount}
                    onChange={(e) => setClaimForm((p) => ({ ...p, claim_amount: e.target.value }))}
                    placeholder="Claim amount (S$)"
                    className="input-admin w-full"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={claimForm.excess_amount}
                    onChange={(e) => setClaimForm((p) => ({ ...p, excess_amount: e.target.value }))}
                    placeholder="Excess (S$)"
                    className="input-admin w-full"
                  />
                  <FormSelect
                    options={[
                      { value: 'true', label: 'NCD affected' },
                      { value: 'false', label: 'NCD not affected' },
                    ]}
                    value={claimForm.ncd_affected}
                    onChange={(v) => setClaimForm((p) => ({ ...p, ncd_affected: v }))}
                    theme="admin"
                    placeholder="NCD impact..."
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={claimForm.private_settlement_amount}
                    onChange={(e) => setClaimForm((p) => ({
                      ...p, private_settlement_amount: e.target.value,
                    }))}
                    placeholder="Private settlement (S$)"
                    className="input-admin w-full"
                  />
                  <input
                    type="url"
                    value={claimForm.video_url}
                    onChange={(e) => setClaimForm((p) => ({ ...p, video_url: e.target.value }))}
                    placeholder="Dashcam / video URL"
                    className="input-admin w-full"
                  />
                  <button
                    type="button"
                    disabled={recordClaim.isPending}
                    onClick={() => {
                      const payload = {
                        id: accident.id,
                        claim_status: claimForm.claim_status || undefined,
                        claim_number: claimForm.claim_number.trim(),
                        claim_amount: claimForm.claim_amount || undefined,
                        excess_amount: claimForm.excess_amount || undefined,
                        fault: claimForm.fault || undefined,
                        private_settlement_amount: claimForm.private_settlement_amount || undefined,
                        video_url: claimForm.video_url.trim(),
                      };
                      if (claimForm.ncd_affected !== '') {
                        payload.ncd_affected = claimForm.ncd_affected === 'true';
                      }
                      recordClaim.mutate(payload);
                    }}
                    className="btn btn-admin-primary sm:col-span-2"
                  >
                    Save Claim Details
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                  <p>Claim #: {accident.claim_number || '—'}</p>
                  <p>Amount: {accident.claim_amount != null ? `$${Number(accident.claim_amount).toFixed(2)}` : '—'}</p>
                  <p>Fault: {accident.fault_display || '—'}</p>
                  <p>Excess: {accident.excess_amount != null ? `$${Number(accident.excess_amount).toFixed(2)}` : '—'}</p>
                </div>
              )}
            </div>
          )}

          {/* Photos */}
          {(accident.photos || []).length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Photos ({accident.photos.length})
              </h3>
              <div className="flex gap-2 flex-wrap">
                {accident.photos.map((p) => (
                  <AccidentPhotoThumb key={p.id} accidentId={accident.id} photo={p} />
                ))}
              </div>
            </div>
          )}

          {/* Link repair */}
          {canAct && (
            <div className="space-y-2 rounded-xl border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-gray-400" />
                Link Workshop Repair
              </h3>
              <FormSelect
                options={maintenanceOptions}
                value={maintenanceLogId}
                onChange={setMaintenanceLogId}
                placeholder={maintenanceOptions.length ? 'Select maintenance log...' : 'No workshop logs for this vehicle'}
                theme="admin"
              />
              <button
                type="button"
                disabled={!maintenanceLogId || linkRepair.isPending}
                onClick={() => linkRepair.mutate({
                  id: accident.id,
                  maintenance_log_id: Number(maintenanceLogId),
                })}
                className="btn btn-admin-outline"
              >
                Link Repair
              </button>
            </div>
          )}

          {/* Third parties */}
          {(accident.third_parties || []).length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Third Parties
              </h3>
              <ul className="space-y-2">
                {accident.third_parties.map((tp) => (
                  <li key={tp.id} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <p className="font-medium text-gray-900">
                      {tp.name || 'Unnamed'} · {tp.party_type_display}
                    </p>
                    <p className="text-xs text-gray-500">
                      {[tp.vehicle_number, tp.phone, tp.insurer_name].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Documents */}
          <div className="flex flex-wrap gap-2">
            {(accident.has_sas || accident.sas_document_url) && (
              <SecureFileLink
                url={assetInventoryAPI.accidentSasDocumentUrl(accident.id)}
                label="View SAS"
                icon={FileText}
              />
            )}
            {accident.private_settlement_document_url && (
              <SecureFileLink
                url={assetInventoryAPI.accidentPrivateSettlementUrl(accident.id)}
                label="Settlement Doc"
              />
            )}
          </div>

          {/* Close */}
          {canAct && (
            <button
              type="button"
              disabled={closeAccident.isPending}
              onClick={() => closeAccident.mutate({ id: accident.id })}
              className="btn btn-admin-primary"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Close Accident
            </button>
          )}

          {status === 'accident_closed' && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900">
              This accident has been closed.
            </div>
          )}

          {/* Cancel */}
          {canAct && (
            cancelling ? (
              <div className="rounded-xl border border-gray-200 p-3 space-y-2">
                <label htmlFor="accident-cancel-reason" className="text-sm font-medium text-gray-900">
                  Why is this accident being cancelled?
                </label>
                <input
                  id="accident-cancel-reason"
                  type="text"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Duplicate filing"
                  className="input-admin w-full"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={!cancelReason.trim() || cancelAccident.isPending}
                    onClick={() => cancelAccident.mutate(
                      { id: accident.id, reason: cancelReason },
                      { onSuccess: () => { setCancelling(false); setCancelReason(''); } },
                    )}
                    className="btn btn-admin-primary"
                  >
                    Confirm Cancellation
                  </button>
                  <button
                    type="button"
                    onClick={() => setCancelling(false)}
                    className="btn btn-admin-outline"
                  >
                    Keep Record
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCancelling(true)}
                className="text-xs text-gray-500 hover:text-red-600 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded inline-flex items-center gap-1"
              >
                <XCircle className="w-3 h-3" />
                Cancel this accident
              </button>
            )
          )}
        </>
      )}
    </Modal>
  );
};

export default AccidentDetailModal;
